import React, { FC, useCallback, useMemo } from 'react';

import { FormikProps } from 'formik';

import {
  AdminAllowanceNames,
  getLongTermContractAttributes,
  IAllLongTermShifts,
} from '@pharmaplan/common';
import { getDaysDistribution } from '@pharmaplan/common/helpers/utils';
import AdminLongTermHourlyRates from '../PharmacyList/AdminCreateWorkshift/AdminLongTermHourlyRates';
import LongTermHourlyRates from '../../SelfService/Workshifts/AddWorkshift/LongTermHourlyRates';
import AllowanceWorkshift from '../../SelfService/Workshifts/AllowanceWorkshift';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { getLongTermHours } from '../../../helpers/Functions';
import CustomTextField from '../../common/CustomTextField';
import useSelfService from '../../../hooks/useSelfService';
import { Constants } from '../../../helpers/Constants';
import strings from '../../../localization';

import styles from './styles';

const { priceSuffix, allowancePlaceholder } = Constants;

interface IExpressBookingAllowances {
  formik: FormikProps<any>;
  isLongTerm?: boolean;
  allShifts?: Array<IAllLongTermShifts>;
  isUpdate?: boolean;
  isEditingBooking?: boolean;
}

const ExpressBookingAllowances: FC<IExpressBookingAllowances> = ({
  formik,
  isLongTerm,
  allShifts,
  isUpdate,
  isEditingBooking = false,
}) => {
  const { isSelfService } = useSelfService();
  const longTerm = useAppSelector(getLongTermContractAttributes);
  const { shiftContractAttributes } = longTerm ?? {};

  const {
    creditsLeft,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    promoHourlyRate,
    professionalCreditsLeft,
    hourlyRateBonus = 0,
  } = formik.values ?? {};

  const pharmacyInfoText = creditsLeft
    ? (strings.formatString(
      strings.promotionalCreditWillBeApplied,
      `${creditsLeft}`,
    ) as string)
    : '';

  const professionalInfoText = professionalCreditsLeft
    ? (strings.formatString(
      strings.promotionalBonusWillBeApplied,
      `${hourlyRateBonus}`,
      `${professionalCreditsLeft}`,
    ) as string)
    : '';

  const selectedShifts = useMemo(() => {
    if (isEditingBooking) return shiftContractAttributes;

    const selectedIds = formik.values?.workshiftIds ?? [];
    const datesSelected = allShifts
      ?.filter((shift) =>
        selectedIds.includes(shift.workShiftId))
      .map((item) =>
        item?.startDate?.split('T')?.[0]);
    const selectedContract = shiftContractAttributes.filter((item) => {
      const date = item.startDate?.split('T')?.[0];
      return datesSelected?.includes(date);
    });

    return selectedContract;
  }, [allShifts, formik.values?.workshiftIds, shiftContractAttributes, isEditingBooking]);

  const { promoDiscountTotal } = useMemo(() => {
    const hours = getLongTermHours({
      shifts: (isUpdate ? selectedShifts : shiftContractAttributes) ?? [],
      weekdayRate: pharmacyWeekDayHourlyRate ?? 0,
      weekendRate: pharmacyWeekendHourlyRate ?? 0,
      credit: creditsLeft ?? 0,
    });

    const weekdayDiscount = (pharmacyWeekDayHourlyRate ?? 0) - (promoHourlyRate ?? 0);
    const weekendDiscount = (pharmacyWeekendHourlyRate ?? 0) - (promoHourlyRate ?? 0);

    return {
      promoDiscountTotal:
        weekdayDiscount * hours.weekdayHours
        + weekendDiscount * hours.weekendHours,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    creditsLeft,
    selectedShifts,
    promoHourlyRate,
    shiftContractAttributes,
  ]);

  const promoBonusTotal = useMemo(() => {
    let hours = { weekdayHours: 0, weekendHours: 0 };

    hours = getLongTermHours({
      shifts: (isUpdate ? selectedShifts : shiftContractAttributes) ?? [],
      weekdayRate: formik.values?.pharmacyWeekDayHourlyRate ?? 0,
      weekendRate: formik.values?.pharmacyWeekendHourlyRate ?? 0,
      credit: professionalCreditsLeft ?? 0,
    });

    return (
      (hours.weekdayHours + hours.weekendHours) * (hourlyRateBonus ?? 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values?.pharmacyWeekDayHourlyRate,
    formik.values?.pharmacyWeekendHourlyRate,
    professionalCreditsLeft,
    hourlyRateBonus,
  ]);

  const { weekdayHours, weekendHours } = useMemo(
    // eslint-disable-next-line consistent-return
    () => {
      if (isUpdate) {
        return getDaysDistribution(selectedShifts ?? []);
      }
    },
    [selectedShifts],
  ) ?? {};

  const LongTermRateComponent = isSelfService
    ? LongTermHourlyRates
    : AdminLongTermHourlyRates;

  const renderHourlyRates = useCallback(
    () =>
      (
        <>
          <CustomTextField
            infoText={professionalInfoText}
            disabled={!!professionalCreditsLeft}
            customClass={styles.horizontalField}
            customLabelStyle={professionalCreditsLeft ? styles.promoLabel : {}}
            suffix={priceSuffix}
            placeholder={allowancePlaceholder}
            horizontal
            id={AdminAllowanceNames.pharmacistHourlyRate}
            formik={formik}
            name={AdminAllowanceNames.pharmacistHourlyRate}
            label={
            strings.formatString(
              strings.hourlyRateFormat,
              strings.professional,
            ) as string
          }
          />
          {!isSelfService && (
          <CustomTextField
            infoText={pharmacyInfoText}
            disabled={!!creditsLeft}
            customLabelStyle={creditsLeft ? styles.promoLabel : {}}
            suffix={priceSuffix}
            customClass={styles.horizontalField}
            placeholder={allowancePlaceholder}
            horizontal
            id={AdminAllowanceNames.pharmacyHourlyRate}
            formik={formik}
            name={AdminAllowanceNames.pharmacyHourlyRate}
            label={strings.pharmacyHourlyRate}
          />
          )}
        </>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creditsLeft, formik, pharmacyInfoText, professionalInfoText],
  );

  return isLongTerm ? (
    <LongTermRateComponent
      hourlyBonus={hourlyRateBonus}
      promoTotal={promoDiscountTotal}
      professionalCreditsLeft={professionalCreditsLeft}
      promoBonusTotal={promoBonusTotal}
      creditsLeft={creditsLeft}
      formik={formik}
      customWeekHours={weekdayHours}
      customWeekendHours={weekendHours}
      customWeekdayLabel={
        strings.formatString(
          strings.userWeekdayRate,
          strings.professional,
        ) as string
      }
      customWeekendLabel={
        strings.formatString(
          strings.userWeekendRate,
          strings.professional,
        ) as string
      }
    />
  ) : (
    <AllowanceWorkshift
      formik={formik}
      isAdmin
      renderHourlyRate={renderHourlyRates()}
      useApi={false}
    />
  );
};

export default ExpressBookingAllowances;
